/**
 * Forgot password — task #17.
 *
 * Two states:
 *   1. Form: email input + submit. Submits to better-auth's
 *      `forgetPassword` endpoint, which (if the email matches an
 *      account) issues a single-use reset token and emails it.
 *   2. Sent: confirmation message. We always show the same success
 *      message regardless of whether the email actually existed —
 *      otherwise the form becomes an account-enumeration oracle.
 *
 * The reset link in the email points at `/reset-password?token=...`,
 * which is the companion route. better-auth verifies the token, accepts
 * a new password, and (because of the `revokeSessionsOnPasswordReset`
 * config in auth.ts) invalidates every other active session for that
 * user.
 */

import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { authClient } from '../lib/auth'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      // better-auth: hits POST /api/v1/auth/request-password-reset
      const result = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (result.error) {
        // Don't surface enumeration-leaking errors. Generic failure
        // message — the only exception is rate-limiting (429), which
        // we want the user to know about so they can retry later.
        if (result.error.status === 429) {
          setError('Too many requests. Try again in a few minutes.')
          return
        }
      }
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-4 text-center">
          <h1 className="text-2xl font-semibold">Check your email</h1>
          <p className="text-muted-foreground">
            If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset
            link. Follow the link in the email to choose a new password.
          </p>
          <p className="text-sm text-muted-foreground">
            Didn&apos;t get an email? Check your spam folder or{' '}
            <button type="button" onClick={() => setSubmitted(false)} className="underline">
              try a different address
            </button>
            .
          </p>
          <Link to="/login" className="text-sm underline block">
            Back to sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="max-w-md w-full space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Reset your password</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your email and we&apos;ll send you a link to choose a new password.
          </p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border rounded-md px-3 py-2"
            placeholder="you@example.com"
          />
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !email}
          className="w-full bg-primary text-primary-foreground rounded-md py-2 disabled:opacity-50"
        >
          {loading ? 'Sending…' : 'Send reset link'}
        </button>

        <Link to="/login" className="text-sm underline text-center block">
          Back to sign in
        </Link>
      </form>
    </div>
  )
}
